class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this.abortController = null;
    this.isSubmitting = false;
    // Falso hasta el primer evento "input" real de ESTA carga de pagina
    // (ver listener dedicado en setupEventListeners, no el debounced) --
    // protege contra el reset de window "load"/pageshow borrando lo que
    // el usuario ya esta escribiendo (conexion lenta en mobile: "load"
    // puede tardar varios segundos y disparar despues de que el usuario
    // ya empezo a tipear).
    this.hasUserTyped = false;

    this.setupEventListeners();

    // Vaciado forzado al construirse -- SOLO el/los buscador(es) del
    // header (sections/header.liquid, id="header"). sections/main-search.
    // liquid (la pagina de resultados) envuelve su propio campo en este
    // MISMO custom element <predictive-search> -- sin este scoping por
    // #header, cualquier reset generico tambien vaciaria ese campo, que
    // debe conservar el termino buscado.
    this.resetHeaderSearchField();
  }

  isHeaderSearch() {
    return !!this.closest("#header");
  }

  // Fuente unica de la condicion "no tocar, el usuario ya lo esta
  // usando": foco actual en el input, o ya escribio algo en esta carga.
  // Usada tanto por resetHeaderSearchField (constructor) como por
  // resetHeaderPredictiveSearch (load/pageshow, mas abajo).
  isInUseByUser() {
    return this.hasUserTyped || document.activeElement === this.input;
  }

  resetHeaderSearchField() {
    if (!this.isHeaderSearch() || this.isInUseByUser()) {
      return;
    }

    this.close(true);
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    // Listener SIN debounce, deliberado: marca hasUserTyped en el mismo
    // evento "input" real, no 300ms despues (el listener debounced de
    // abajo). Un reset de window "load" podria dispararse dentro de esa
    // ventana de 300ms, antes de que el debounce llegue a correr.
    this.input.addEventListener("input", () => {
      this.hasUserTyped = true;
    });

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    // Falla "reaparecen tras Enter", segunda causa: el debounce de 300ms
    // del listener "input" es independiente del AbortController -- si el
    // usuario presiona Enter ANTES de que se cumplan esos 300ms, el
    // submit (y su abort()) ya ocurrio cuando el timer del debounce recien
    // dispara este onChange, creando un fetch NUEVO que el abort() previo
    // nunca pudo anticipar (no existia todavia). isSubmitting corta esto
    // en el origen: mientras el formulario esta en curso de enviarse,
    // ninguna llamada a onChange hace nada, sin importar cuando dispare
    // el timer del debounce.
    if (this.isSubmitting) {
      return;
    }

    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Falla "los resultados reaparecen despues de Enter": el submit nativo
    // (GET a la pagina de resultados) no cancelaba el fetch predictivo en
    // vuelo, disparado antes por el input debounced -- si esa respuesta
    // llegaba durante la breve ventana antes de que la navegacion
    // reemplace el documento, renderSearchResults() volvia a abrir el
    // panel. Se aborta cualquier fetch pendiente, se marca isSubmitting
    // (ver onChange/getSearchResults/renderSearchResults arriba/abajo) y
    // se cierra el panel de inmediato, antes de dejar que la navegacion
    // continue.
    this.isSubmitting = true;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.close();
  }

  onFocus() {
    // El campo puede llegar con un termino ya escrito por Liquid --
    // value="{{ search.terms }}" en sections/main-search.liquid (pagina
    // de resultados) es el caso real. Si el usuario TODAVIA no escribio
    // nada en esta interaccion (hasUserTyped sigue false), tocar/enfocar
    // el campo lo deja vacio para escribir de cero, en vez de reabrir
    // resultados del termino anterior o dejar que lo nuevo se sume a ese
    // texto. Sin scoping a #header a proposito: aplica a TODAS las
    // instancias, incluida la de main-search.liquid -- ahi el termino
    // puede seguir visible al cargar la pagina, pero al tocar el campo
    // debe vaciarse igual que en el header.
    if (!this.hasUserTyped && this.getQuery().length) {
      this.close(true);
      return;
    }

    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    if (this.isSubmitting) {
      return;
    }

    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    // Una peticion nueva cancela cualquier peticion predictiva anterior
    // todavia en vuelo (ver tambien onFormSubmit, que aborta al enviar el
    // formulario) -- evita que una respuesta tardia de una busqueda ya
    // reemplazada reabra el panel con resultados obsoletos.
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`,
      { signal: this.abortController.signal },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // AbortError es esperado (abort intencional, ver arriba) -- no es
        // una falla real, no debe cerrar nada de nuevo ni loguear ruido.
        if (error.name === "AbortError") {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Defensa adicional: si isSubmitting ya se activo (guards de arriba en
    // onChange/getSearchResults), pero esta llamada viene de un .then() de
    // un fetch que ya habia arrancado ANTES del submit y que el abort()
    // no alcanzo a cancelar a tiempo (red ya habia respondido), igual no
    // debe reabrir el panel.
    if (this.isSubmitting) {
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);

// Vaciado forzado -- por instruccion explicita, sin depender del
// diagnostico anterior (value="" en el render de Liquid + reset en
// construccion pueden no alcanzar si el propio navegador restaura el
// valor del input DESPUES de esos puntos, ej. autofill/restauracion de
// formularios tras DOMContentLoaded). Dos momentos adicionales, ambos
// SIEMPRE (no condicionados) y acotados a "#header predictive-search" --
// nunca al buscador propio de sections/main-search.liquid (pagina de
// resultados), que reutiliza el mismo custom element y debe conservar su
// termino.
function resetHeaderPredictiveSearch() {
  document.querySelectorAll("#header predictive-search").forEach((instance) => {
    // isSubmitting siempre en false aqui, ANTES del guard de abajo y sin
    // importar si se omite el resto -- un isSubmitting=true colgado deja
    // muerta la busqueda predictiva (onChange/getSearchResults/
    // renderSearchResults lo revisan primero y no hacen nada), y nada
    // mas en este archivo lo resetea a false salvo un submit nuevo.
    instance.isSubmitting = false;

    // No tocar el resto (valor del input, panel) si el usuario ya esta
    // usando este campo -- mismo guard que resetHeaderSearchField()
    // (constructor), ver isInUseByUser() arriba: foco actual en el
    // input, o ya escribio algo en esta carga. En mobile con conexion
    // lenta, "load" puede tardar varios segundos y disparar mientras el
    // usuario ya esta tipeando.
    if (typeof instance.isInUseByUser === "function" && instance.isInUseByUser()) {
      return;
    }

    if (typeof instance.close === "function") {
      instance.close(true);
    }
  });
}

// 2. window "load": el navegador puede restaurar valores de formularios
// despues de DOMContentLoaded (y por lo tanto despues del reset en el
// constructor, que corre durante el upgrade del custom element).
window.addEventListener("load", resetHeaderPredictiveSearch);

// 3. "pageshow". persisted:true (bfcache, boton atras) fuerza el reset
// SIN pasar por isInUseByUser(): al volver atras justo despues de tipear
// Enter, el foco tipicamente sigue en el input (causa real de que el
// guard de foco lo tratara como "en uso" y omitiera todo el reset,
// dejando el texto anterior Y isSubmitting=true colgado -- la busqueda
// predictiva quedaba muerta). persisted:false sigue pasando por
// resetHeaderPredictiveSearch() (con su guard normal).
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    document.querySelectorAll("#header predictive-search").forEach((instance) => {
      instance.hasUserTyped = false;
      instance.isSubmitting = false;

      if (typeof instance.close === "function") {
        instance.close(true);
      }

      if (instance.input) {
        instance.input.blur();
      }
    });
    return;
  }

  resetHeaderPredictiveSearch();
});
